import { ref, computed } from "vue";
import { useCustomerApi } from "@/services/api/useCustomerApi";
import type { Organization } from "@/stores/tender/types";

export interface OrganizationBypassError {
  type: "offline" | "missing" | "error";
  message: string;
}

export const useOrganizationBypass = () => {
  const { getOrganizationsFromOrgNumbers } = useCustomerApi();

  const isLoading = ref(false);
  const error = ref<OrganizationBypassError | null>(null);
  const organization = ref<Organization | null>(null);

  const bypassReallyInvoices = computed(() => {
    return organization.value?.bypassReallyInvoices ?? false;
  });

  const fetchOrganizationBypass = async (orgNumber: string) => {
    if (!orgNumber) {
      error.value = {
        type: "missing",
        message: "Organisasjonsnummer mangler",
      };
      return false;
    }

    isLoading.value = true;
    error.value = null;
    organization.value = null;

    try {
      const response = await getOrganizationsFromOrgNumbers(orgNumber);

      if (response?.data && response.data.length > 0) {
        // Find the organization with the matching org number
        const foundOrg = response.data.find(
          (org) => org.orgNumber === orgNumber,
        );

        if (foundOrg) {
          organization.value = foundOrg;
          return foundOrg.bypassReallyInvoices;
        } else {
          error.value = {
            type: "missing",
            message: `Organisasjon med organisasjonsnummer ${orgNumber} ble ikke funnet`,
          };
          return false;
        }
      } else {
        error.value = {
          type: "missing",
          message: `Ingen organisasjoner funnet for organisasjonsnummer ${orgNumber}`,
        };
        return false;
      }
    } catch (err) {
      console.error("Error fetching organization bypass setting:", err);
      error.value = {
        type: "error",
        message: "Kunne ikke hente organisasjonsinformasjon",
      };
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const reset = () => {
    isLoading.value = false;
    error.value = null;
    organization.value = null;
  };

  return {
    isLoading,
    error,
    organization,
    bypassReallyInvoices,
    fetchOrganizationBypass,
    reset,
  };
};
